import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router';
import { ErrorState, LoadingState } from '@/components/ui';
import { authApi } from './auth-api';
import { useMe, useSession } from './session-context';

/** Recheck access before mounting the next page, including full-screen pages. */
export function RefreshSessionOnNavigation() {
  const { pathname } = useLocation();
  // The index redirect is the same page. Search params drive filters/drawers,
  // so changing them must not unmount the page or interrupt typing.
  const page = pathname === '/' ? '/dashboard' : pathname;
  const me = useMe();
  const { updateMe } = useSession();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  // Login/restore/role selection already supplies fresh `me` for the first page.
  const [check, setCheck] = useState<{
    page: string;
    checked: boolean;
    attempt: number;
    error?: unknown;
  }>({ page, checked: true, attempt: 0 });
  const { page: checkingPage, checked, attempt, error } = check;
  // A new pathname needs its own check even if the user goes back before an
  // earlier check finishes. Update during render so no stale page can mount.
  if (checkingPage !== page) setCheck({ page, checked: false, attempt: 0 });

  useEffect(() => {
    if (checkingPage !== page || checked) return;
    let current = true;
    void authApi
      .me()
      .then((next) => {
        if (!current) return;
        // A changed capability/branch scope must not reuse data cached under
        // the old access. Role-list-only changes need no cache reset.
        const scope = (user: typeof next) =>
          JSON.stringify([
            user.activeRole?.key,
            user.permissions,
            user.branches,
            user.allBranches,
            user.canViewAllBranches,
          ]);
        if (scope(me) !== scope(next)) queryClient.clear();
        updateMe(next);
        setCheck({ page, checked: true, attempt: 0 });
      })
      .catch((error: unknown) => {
        if (current) setCheck({ page, checked: false, attempt, error });
      });
    // Ignore an older navigation response or a response after sign-out.
    return () => {
      current = false;
    };
  }, [page, checkingPage, checked, attempt, me, updateMe, queryClient]);

  if (checkingPage !== page || !checked) {
    if (checkingPage === page && error)
      return (
        <ErrorState
          title={t('auth.refreshFailed')}
          error={error}
          onRetry={() =>
            setCheck({ page, checked: false, attempt: attempt + 1 })
          }
        />
      );
    return <LoadingState label={t('auth.refreshingAccess')} />;
  }
  return <Outlet />;
}
